import { apiGet, apiPost } from '@/lib/api/client.js';

export type ReportPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export type ReportQuery = {
  period?: ReportPeriod;
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
  groupBy?: 'day' | 'week' | 'month';
  date?: string;
};

function qString(q: ReportQuery): string {
  const p = new URLSearchParams();
  if (q.period) p.set('period', q.period);
  if (q.dateFrom) p.set('dateFrom', q.dateFrom);
  if (q.dateTo) p.set('dateTo', q.dateTo);
  if (q.storeId) p.set('storeId', q.storeId);
  if (q.groupBy) p.set('groupBy', q.groupBy);
  if (q.date) p.set('date', q.date);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function fetchReportDashboard(q: ReportQuery = {}) {
  return apiGet(`/api/v1/reports/dashboard${qString(q)}`).then((r) => r.data);
}

export async function fetchReportRevenue(q: ReportQuery) {
  return apiGet(`/api/v1/reports/revenue${qString(q)}`).then((r) => r.data);
}

export async function fetchReportTickets(q: ReportQuery) {
  return apiGet(`/api/v1/reports/tickets${qString(q)}`).then((r) => r.data);
}

export async function fetchReportTechnicians(q: ReportQuery) {
  return apiGet(`/api/v1/reports/technicians${qString(q)}`).then((r) => r.data);
}

export async function fetchReportRepairs(q: ReportQuery) {
  return apiGet(`/api/v1/reports/repairs${qString(q)}`).then((r) => r.data);
}

export async function fetchReportInventory(q: Pick<ReportQuery, 'storeId'>) {
  return apiGet(`/api/v1/reports/inventory${qString(q)}`).then((r) => r.data);
}

export async function fetchReportEndOfDay(q: Pick<ReportQuery, 'storeId' | 'date'>) {
  return apiGet(`/api/v1/reports/end-of-day${qString(q)}`).then((r) => r.data);
}

export async function fetchReportMultiStore(q: ReportQuery) {
  return apiGet(`/api/v1/reports/multi-store${qString(q)}`).then((r) => r.data);
}

export type LeaderboardPeriod = 'today' | 'week' | 'month';

export type LeaderboardRanking = {
  rank: number;
  userId: string;
  firstName: string;
  lastName: string;
  avatarInitials: string;
  ticketsCompleted: number;
  ticketsInProgress: number;
  avgResolutionHours: number;
  totalLaborMinutes: number;
  billableLaborMinutes: number;
  warrantyCallbacks: number;
  npsAvgScore: number | null;
  performanceScore: number;
  trend: 'up' | 'down' | 'same';
};

export type LeaderboardData = {
  period: LeaderboardPeriod;
  generatedAt: string;
  rankings: LeaderboardRanking[];
  storeStats: {
    totalTicketsCompleted: number;
    totalRevenue: string;
    avgNpsScore: number | null;
  };
};

export async function getLeaderboard(period: LeaderboardPeriod = 'week', storeId?: string) {
  const p = new URLSearchParams({ period });
  if (storeId) p.set('storeId', storeId);
  return apiGet<LeaderboardData>(`/api/v1/reports/leaderboard?${p.toString()}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Sprint 6 — Mission control dashboard                               */
/* ------------------------------------------------------------------ */

export type DashboardLiveData = {
  generatedAt: string;
  today: {
    revenue: string;
    ticketsCreated: number;
    ticketsClosed: number;
    avgTicketValue: string;
    nps: { avg: number | null; count: number };
  };
  outstanding?: {
    total: string;
    count: number;
  };
  goals: {
    daily: { goal: string; actual: string; pct: number };
    weekly: { goal: string; actual: string; pct: number };
    monthly: { goal: string; actual: string; pct: number };
  };
  queue: {
    overdue: number;
    dueSoon: number;
    ready: number;
    overdueTickets: Array<{ id: string; ticketNumber: string; promisedDate: string | null }>;
    dueSoonTickets: Array<{ id: string; ticketNumber: string; promisedDate: string | null }>;
    readyTickets: Array<{ id: string; ticketNumber: string; readyAt: string | null }>;
  };
  cashSession: {
    isOpen: boolean;
    openedAt: string | null;
    openedByName: string | null;
    expectedCash: string | null;
  };
  team: Array<{
    userId: string;
    name: string;
    ticketId: string;
    ticketNumber: string;
    startedAt: string | null;
  }>;
  alerts: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    message: string;
    link?: string;
  }>;
  revenueBySource: Array<{ source: string; amount: string; pct: number }>;
  paymentMethods: { cash: string; card: string; storeCredit: string; other: string };
  last7Days: Array<{ date: string; revenue: string; tickets: number }>;
  subscriptions?: {
    active: number;
    mrr: string;
    dueToday: number;
    pastDue: number;
  };
};

export async function fetchDashboardLive(storeId?: string) {
  const p = storeId ? `?storeId=${storeId}` : '';
  return apiGet<DashboardLiveData>(`/api/v1/reports/dashboard/live${p}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Profit & Loss                                                      */
/* ------------------------------------------------------------------ */

export type ProfitLossData = {
  period: { start: string; end: string };
  revenue: { repairs: string; retail: string; other: string; total: string };
  cogs: { parts: string; total: string };
  grossProfit: string;
  grossMarginPct: number;
  expenses: Array<{ category: string; amount: string }>;
  totalExpenses: string;
  netProfit: string;
  netMarginPct: number;
  chart: Array<{ date: string; revenue: string; expenses: string; netProfit: string }>;
  costCompleteness?: {
    importedTickets: number;
    withCost: number;
    pct: number;
    message: string | null;
  };
};

export async function fetchReportProfitLoss(q: ReportQuery) {
  return apiGet<ProfitLossData>(`/api/v1/reports/profit-loss${qString(q)}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Customer lifetime value                                            */
/* ------------------------------------------------------------------ */

export type CustomerLtvData = {
  summary: {
    avgLtv: string;
    avgOrdersPerCustomer: number;
    avgOrderValue: string;
    totalCustomers: number;
    repeatRate: number;
  };
  segments: Array<{ segment: string; customerCount: number; avgLtv: string; totalRevenue: string }>;
  topCustomers: Array<{
    id: string;
    name: string;
    totalSpend: string;
    totalTickets: number;
    lastVisitAt: string | null;
    ltv: string;
  }>;
  chart: Array<{ cohort: string; avgLtv: string }>;
};

export async function fetchReportCustomerLtv(q: ReportQuery) {
  return apiGet<CustomerLtvData>(`/api/v1/reports/customer-lifetime-value${qString(q)}`).then(
    (r) => r.data,
  );
}

/* ------------------------------------------------------------------ */
/* Inventory intelligence                                             */
/* ------------------------------------------------------------------ */

export type InventoryIntelligenceData = {
  summary: { deadStockValue: string; deadStockCount: number; forecastedStockoutCount: number; excessStockValue: string };
  deadStock: Array<{ itemId: string; name: string; qtyOnHand: number; value: string; daysSinceLastMove: number }>;
  reorderSuggestions: Array<{
    itemId: string;
    name: string;
    currentStock: number;
    avgWeeklyUsage: number;
    daysUntilStockout: number | null;
    suggestedReorderQty: number;
  }>;
  marginByCategory: Array<{ category: string; revenue: string; cost: string; marginPct: number }>;
};

export async function fetchReportInventoryIntelligence(q: Pick<ReportQuery, 'storeId'>) {
  return apiGet<InventoryIntelligenceData>(
    `/api/v1/reports/inventory/intelligence${qString(q)}`,
  ).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Acquisition                                                        */
/* ------------------------------------------------------------------ */

export type AcquisitionData = {
  summary: { totalNewCustomers: number; topSource: string | null };
  bySource: Array<{ source: string; count: number; pct: number; revenue: string }>;
  chart: Array<{ date: string; count: number }>;
};

export async function fetchReportAcquisition(q: ReportQuery) {
  return apiGet<AcquisitionData>(`/api/v1/reports/acquisition${qString(q)}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Cash flow                                                          */
/* ------------------------------------------------------------------ */

export type CashFlowData = {
  summary: {
    netCashFlow: string;
    totalInflow: string;
    totalOutflow: string;
    varianceTotal?: string;
    overToleranceCount?: number;
    cashVarianceTolerance?: string;
  };
  byMethod: Array<{ method: string; inflow: string; outflow: string }>;
  chart: Array<{ date: string; inflow: string; outflow: string; net: string }>;
  cashSessions: Array<{
    id: string;
    openedAt: string;
    closedAt: string | null;
    expectedCash: string;
    actualCash: string | null;
    variance: string | null;
    overTolerance?: boolean;
    closedByName?: string | null;
    openedByName?: string | null;
  }>;
  varianceByEmployee?: Array<{
    userId: string;
    name: string;
    sessionCount: number;
    varianceTotal: string;
    overToleranceCount: number;
  }>;
};

export async function fetchReportCashFlow(q: ReportQuery) {
  return apiGet<CashFlowData>(`/api/v1/reports/cash-flow${qString(q)}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Parts forecast                                                     */
/* ------------------------------------------------------------------ */

export type PartsForecastItem = {
  itemId?: string;
  name: string;
  sku?: string | null;
  avgWeeklyUsage?: number;
  currentStock: number;
  daysUntilStockout?: number | null;
  suggestedReorderQty: number;
  suggestedReorderDate?: string | null;
  forecastDemand?: number;
  risk?: string;
};

export type PartsForecastData = {
  generatedAt: string | null;
  items: PartsForecastItem[];
  status?: string;
  reportId?: string;
};

export async function generatePartsForecast(_body: { storeId?: string } = {}) {
  return apiPost<{ reportId: string; jobId: string; status: string }>(
    '/api/v1/reports/parts-forecast/generate',
    {},
  ).then((r) => r.data);
}

export async function fetchPartsForecastStatus(jobId: string) {
  return apiGet<{ status: string; reportId?: string }>(
    `/api/v1/reports/parts-forecast/status/${jobId}`,
  ).then((r) => r.data);
}

export async function fetchPartsForecastReport(reportId: string) {
  return apiGet<{
    id: string;
    status: string;
    result: { items?: PartsForecastItem[]; generatedAt?: string } | null;
    createdAt: string;
    completedAt: string | null;
  }>(`/api/v1/reports/parts-forecast/${reportId}`).then((r) => r.data);
}

/** Convenience: generate then poll until complete (or timeout). */
export async function fetchPartsForecast(_q?: Pick<ReportQuery, 'storeId'>): Promise<PartsForecastData> {
  const started = await generatePartsForecast();
  const deadline = Date.now() + 60_000;
  let jobId = started.jobId;
  let reportId = started.reportId;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 1500));
    const status = await fetchPartsForecastStatus(jobId);
    if (status.reportId) reportId = status.reportId;
    if (status.status === 'completed' || status.status === 'complete') break;
    if (status.status === 'failed') {
      throw new Error('Parts forecast job failed');
    }
  }

  const report = await fetchPartsForecastReport(reportId);
  const result = report.result ?? {};
  const items = Array.isArray(result.items) ? result.items : [];
  return {
    generatedAt: result.generatedAt ?? report.completedAt ?? report.createdAt,
    items,
    status: report.status,
    reportId: report.id,
  };
}

/* ------------------------------------------------------------------ */
/* Referrals                                                          */
/* ------------------------------------------------------------------ */

export type ReferralsData = {
  summary: {
    totalReferrals: number;
    convertedReferrals: number;
    conversionRate: number;
    totalRevenueFromReferrals: string;
  };
  topReferrers: Array<{
    customerId: string;
    name: string;
    referralCount: number;
    revenueGenerated: string;
  }>;
  chart: Array<{ date: string; count: number }>;
};

export async function fetchReportReferrals(q: ReportQuery) {
  return apiGet<ReferralsData>(`/api/v1/reports/referrals${qString(q)}`).then((r) => r.data);
}

/* ------------------------------------------------------------------ */
/* Outstanding balances (AR)                                          */
/* ------------------------------------------------------------------ */

export type OutstandingBalanceRow = {
  id: string;
  ticketNumber: string;
  recordType: 'ticket' | 'sale';
  customerId: string | null;
  customerName: string;
  phone: string | null;
  createdAt: string;
  ageDays: number;
  ageBucket: 'current' | '1-30' | '31-60' | '61-90' | '90+';
  total: string;
  amountPaid: string;
  balanceDue: string;
  status: string;
  assignedTech: string | null;
};

export type OutstandingBalancesData = {
  summary: {
    totalOutstanding: string;
    count: number;
    ticketsOutstanding: string;
    ticketsCount: number;
    salesOutstanding: string;
    salesCount: number;
    aging: Record<
      'current' | '1-30' | '31-60' | '61-90' | '90+',
      { count: number; total: string }
    >;
  };
  rows: OutstandingBalanceRow[];
};

export type OutstandingBalancesQuery = ReportQuery & {
  recordType?: 'all' | 'tickets' | 'sales';
  customer?: string;
  minBalance?: number;
  ageBucket?: 'all' | 'current' | '1-30' | '31-60' | '61-90' | '90+';
};

export async function fetchReportOutstandingBalances(q: OutstandingBalancesQuery) {
  const p = new URLSearchParams();
  if (q.period) p.set('period', q.period);
  if (q.dateFrom) p.set('dateFrom', q.dateFrom);
  if (q.dateTo) p.set('dateTo', q.dateTo);
  if (q.storeId) p.set('storeId', q.storeId);
  if (q.recordType) p.set('recordType', q.recordType);
  if (q.customer) p.set('customer', q.customer);
  if (q.minBalance != null) p.set('minBalance', String(q.minBalance));
  if (q.ageBucket) p.set('ageBucket', q.ageBucket);
  const s = p.toString();
  return apiGet<OutstandingBalancesData>(
    `/api/v1/reports/outstanding-balances${s ? `?${s}` : ''}`,
  ).then((r) => r.data);
}
